import { useState, useEffect, useCallback, useMemo } from 'react'
import { ConnectionProvider, WalletProvider } from '@solana/wallet-adapter-react'
import { PhantomWalletAdapter } from '@solana/wallet-adapter-phantom'
import { SolflareWalletAdapter } from '@solana/wallet-adapter-solflare'
import { useAuth } from './auth/AuthContext'
import { Header } from './components/layout/Header'
import type { AppTab } from './components/layout/Header'
import { PortfolioOverview } from './components/dashboard/PortfolioOverview'
import type { PanelAgg } from './components/dashboard/PortfolioOverview'
import type { PortfolioSummary } from './types/portfolio'
import type { StageAllocation } from './types/strategy'
import type { PortfolioDeltaMessage } from './types/delta'
import { usePortfolioStream } from './ws/usePortfolioStream'
import { fetchPortfolioPositions } from './api/portfolio'
import { SigningPage } from './components/auth/SigningPage'
import { ConnectWalletButton } from './components/auth/ConnectWalletButton'
import type { WalletType } from './auth/useWalletAuth'
import { MarketReportPanel } from './components/dashboard/MarketReportPanel'
import StrategyAllocationPanel from './components/dashboard/StrategyAllocationPanel'
import SubscriptionPanel from './components/dashboard/SubscriptionPanel'
import LpPanel from './components/dashboard/LpPanel'
import WalletPanel from './components/dashboard/WalletPanel'
import LendingPanel from './components/dashboard/LendingPanel'
import PerpetualsPanel from './components/dashboard/PerpetualsPanel'
import LiveMarketPanel from './components/dashboard/LiveMarketPanel'
import SolGamesPanel from './components/dashboard/SolGamesPanel'
import { PendingReportsProvider } from './context/PendingReportsContext'
import { NoticeStack } from './components/common/NoticeStack'
import TradeView from './components/trade/TradeView'
import { useWalletAdapterBridge } from './lib/walletBridge'
import { WizardView } from './components/wizard/WizardView'


type LendingUsd = { lendingUsd: number; borrowUsd: number }

/**
 * Mounts the wallet-adapter bridge at the app shell so the `@solana/wallet-adapter-react`
 * session is re-established as soon as the user is authenticated, regardless of which
 * tab they land on. Without this, the bridge only runs inside TradeView / capital /
 * dashboard panels — creating a race where a form can mount before the bridge has
 * finished `select()` + `connect()`, throwing "Wallet not connected".
 * Renders nothing.
 */
function WalletBridgeMount() {
  useWalletAdapterBridge()
  return null
}

/** Pulsing placeholder that preserves grid layout while the first batch fetch loads */
function PanelSkeleton({ className }: { className?: string }) {
  return (
    <div className={`bg-surface-card rounded-xl border border-fringe animate-pulse ${className ?? ''}`} />
  )
}

function TrademarkFooter() {
  return (
    <footer className="mx-auto mt-10 mb-6 w-full max-w-4xl text-center px-4">
      <p className="text-[11px] text-ink-muted/80 leading-relaxed">
        Solquant is an independent application. &ldquo;Solana&rdquo; and &ldquo;SOL&rdquo;
        are trademarks of the Solana Foundation. Solquant is not affiliated with,
        endorsed by, or sponsored by the Solana Foundation.
      </p>
    </footer>
  )
}

function Dashboard({ portfolioId, onSummaryUpdate }: { portfolioId: string; onSummaryUpdate: (s: PortfolioSummary) => void }) {
  const { token } = useAuth()
  const [partialAgg, setPartialAgg] = useState<Partial<PanelAgg>>({})
  const [portfolioReady, setPortfolioReady] = useState(false)
  const [polledDelta, setPolledDelta] = useState<PortfolioDeltaMessage | null>(null)
  // Gate the 4 position panels: render only after the first batch fetch resolves so they
  // find wsData already populated at mount and skip their individual REST fallback fetches.
  const [positionsReady, setPositionsReady] = useState(false)

  // Single WebSocket connection for the entire dashboard — server pushes all data
  const { latest: wsLatest } = usePortfolioStream(portfolioId, token)

  // WS delivering data also counts as ready
  useEffect(() => {
    if (wsLatest) setPositionsReady(true)
  }, [wsLatest])

  // 10s batch poll — single combined endpoint replaces 4 per-panel polls.
  // WS takes priority when available; poll runs as a silent fallback.
  //
  // The backend is the source of truth: an empty bucket in a successful poll response means
  // the backend confirmed there is nothing there (adapters only ever write a confirmed empty,
  // never on a failed/unconfirmed fetch — see JupiterPortfolioAdapter's empty-confirm streak).
  // Discarding empty responses here used to leave a closed LP position (or a repaid loan)
  // showing indefinitely whenever the WS was down and the poll fallback was doing the rendering.
  useEffect(() => {
    if (!portfolioId) { setPositionsReady(false); return }
    const run = () => {
      fetchPortfolioPositions(portfolioId)
        .then(data => { setPolledDelta(data) })
        .catch(e => console.warn('positions poll failed', e))
        .finally(() => setPositionsReady(true)) // mark ready whether data arrived or not
    }
    run()
    const id = setInterval(run, 10_000)
    return () => clearInterval(id)
  }, [portfolioId])

  // WS takes priority over poll
  const delta = wsLatest ?? polledDelta

  // Notify header when summary updates
  useEffect(() => {
    if (delta) onSummaryUpdate(delta)
  }, [delta])

  const [lendStage, setLendStage] = useState<StageAllocation | null>(null)
  const [borrowStage, setBorrowStage] = useState<StageAllocation | null>(null)
  const [lpStage, setLpStage] = useState<StageAllocation | null>(null)
  const [hedgeStage, setHedgeStage] = useState<StageAllocation | null>(null)

  const handleLendBorrowStages = useCallback((lend: StageAllocation | null, borrow: StageAllocation | null) => {
    setLendStage(lend)
    setBorrowStage(borrow)
  }, [])

  const handleLpStage = useCallback((lp: StageAllocation | null) => {
    setLpStage(lp)
  }, [])

  const handleHedgeStage = useCallback((hedge: StageAllocation | null) => {
    setHedgeStage(hedge)
  }, [])

  const handlePanelUsd = useCallback((key: 'wallet' | 'lp' | 'perp' | 'lending' | 'claimable', value: number | LendingUsd) => {
    setPartialAgg(prev => {
      if (key === 'lending') {
        const { lendingUsd, borrowUsd } = value as LendingUsd
        return { ...prev, lendingUsd, borrowUsd }
      }
      return { ...prev, [key + 'Usd']: value as number }
    })
  }, [])

  return (
    <div>
      <main className="mx-auto max-w-7xl px-2 py-4 sm:px-4 md:px-6 space-y-3 sm:space-y-5">

        {/* Row 1: [Portfolio Summary + Wallet stacked] | [Strategy Allocation] */}
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-3 sm:gap-5 items-stretch">
          {/* h-full on the inner flex column gives the chart wrapper a real height
              context so its flex-1 can stretch the LiveMarketPanel to match the
              right-side StrategyAllocationPanel. */}
          <div className="flex flex-col gap-3 sm:gap-5 h-full">
            <PortfolioOverview
              portfolioId={portfolioId}
              agg={partialAgg}
              wsUpdate={delta}
              onPortfolioReady={() => setPortfolioReady(true)}
              onSummaryUpdate={onSummaryUpdate}
            />
            <div className="flex-1 min-h-0">
              <LiveMarketPanel lpPositions={delta?.lp} />
            </div>
          </div>
          <StrategyAllocationPanel ready={portfolioReady} wsWallet={delta?.wallet} wsLending={delta?.lending} wsLp={delta?.lp} wsPerps={delta?.perps} onLendBorrowStages={handleLendBorrowStages} onLpStage={handleLpStage} onHedgeStage={handleHedgeStage} />
        </div>

        {/* Row 2: [Lending/Borrow] | [Raydium Concentrated LP] */}
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-3 sm:gap-5 items-stretch">
          {positionsReady
            ? <LendingPanel onPanelUsd={handlePanelUsd} wsData={delta?.lending} lendStage={lendStage} borrowStage={borrowStage} />
            : <PanelSkeleton className="min-h-[120px]" />}
          {positionsReady
            ? <LpPanel onPanelUsd={handlePanelUsd} wsData={delta?.lp} lpStage={lpStage} />
            : <PanelSkeleton className="min-h-[120px]" />}
        </div>

        {/* Row 3: [Wallet] | [Perpetuals] */}
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-3 sm:gap-5 items-stretch">
          {positionsReady
            ? <WalletPanel onPanelUsd={handlePanelUsd} wsData={delta?.wallet} />
            : <PanelSkeleton className="min-h-[120px]" />}
          {positionsReady
            ? <PerpetualsPanel onPanelUsd={handlePanelUsd} wsData={delta?.perps} hedgeStage={hedgeStage} />
            : <PanelSkeleton className="min-h-[120px]" />}
        </div>

      </main>
    </div>
  )
}

function MarketResearchView() {
  return (
    <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
      <MarketReportPanel />
    </main>
  )
}

function SolGamesView() {
  return (
    <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
      <SolGamesPanel />
    </main>
  )
}

function SettingsView() {
  return (
    <main className="mx-auto max-w-3xl px-4 py-6 sm:px-6">
      <SubscriptionPanel />
    </main>
  )
}

const RPC_RAW = import.meta.env.VITE_SOLANA_RPC_URL || '/api/rpc'

function WelcomePage() {
  const { sessionExpiredMessage } = useAuth()
  return (
    <div className="flex flex-col items-center justify-center min-h-[calc(100vh-56px)] px-6 text-center">
      <img src="/logo.svg" width="320" height="320" alt="" aria-hidden="true" className="mb-8 opacity-95 animate-logo-float" />
      <h1 className="font-display text-3xl font-bold text-ink mb-2">Welcome to SolQuant</h1>
      {sessionExpiredMessage && (
        <div className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-300 max-w-sm">
          {sessionExpiredMessage}
        </div>
      )}
      <p className="text-sm text-ink-muted max-w-sm mb-8 leading-relaxed">
        Real-time LP health, liquidation warnings, and loop eligibility — connect your wallet to get started.
      </p>
      <ConnectWalletButton label="Connect Wallet" />
    </div>
  )
}

function UnauthGate() {
  // Capture once at mount — history.replaceState inside SigningPage cleans the
  // URL before auth completes, so reading window.location.search on every
  // render would see an empty string and fire the welcome page mid-flow.
  const [signinType] = useState<WalletType | null>(() => {
    const p = new URLSearchParams(window.location.search)
    const v = p.get('signin')
    return v === 'phantom' || v === 'solflare' ? v : null
  })

  if (signinType) return <SigningPage walletType={signinType} />
  return <WelcomePage />
}

export function App() {
  const { isAuthenticated, portfolioId, token } = useAuth()
  const [tab, setTab] = useState<AppTab>('dashboard')
  const [summary, setSummary] = useState<PortfolioSummary | null>(null)

  // Shared WS stream used by WizardView when the earn tab is active.
  // Dashboard has its own WS connection while mounted; since dashboard and earn tabs are
  // mutually exclusive, this does not create duplicate connections.
  const { latest: appWsLatest } = usePortfolioStream(
    tab === 'earn' && isAuthenticated && !!portfolioId ? portfolioId : null,
    token,
  )

  const endpoint = useMemo(
    () => RPC_RAW.startsWith('http') ? RPC_RAW : `${window.location.origin}${RPC_RAW}`,
    [],
  )
  const wallets = useMemo(() => [new PhantomWalletAdapter(), new SolflareWalletAdapter()], [])

  // Clear summary on logout
  useEffect(() => { if (!isAuthenticated) setSummary(null) }, [isAuthenticated])

  // Cast needed: wallet-adapter types target React 19 but we use React 18
  const Conn = ConnectionProvider as any
  const Wallet = WalletProvider as any

  return (
    <Conn endpoint={endpoint}>
      <Wallet wallets={wallets} autoConnect>
        <WalletBridgeMount />
        <PendingReportsProvider>
          <div className="min-h-screen bg-surface">
            <Header activeTab={tab} onTabChange={setTab} showTabs={isAuthenticated && !!portfolioId} summary={summary} />
            {isAuthenticated && portfolioId ? (
              <>
                {tab === 'dashboard'  && <Dashboard portfolioId={portfolioId} onSummaryUpdate={setSummary} />}
                {tab === 'market'     && <MarketResearchView />}
                {tab === 'earn'       && <WizardView portfolioId={portfolioId} wsLp={appWsLatest?.lp} />}
                {tab === 'trade'      && <TradeView />}
                {tab === 'games'      && <SolGamesView />}
                {tab === 'settings'   && <SettingsView />}
                <TrademarkFooter />
              </>
            ) : (
              <UnauthGate />
            )}
            <NoticeStack />
          </div>
        </PendingReportsProvider>
      </Wallet>
    </Conn>
  )
}
